import React from 'react'
import { FormField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AddEditTeam } from 'api/Identity/Types/AddUpdateTeam'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { CountryDto } from 'api/Identity/Types/Country'
import { RegionDto } from 'api/Identity/Types/Region'
import { TeamDto } from 'api/Identity/Types/Team'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

type Option = 'Custom' | 'Region' | 'Country'

export interface AddEditTeamFormValues {
  id?: string
  type: Option
  name: string | null
  country: string | undefined
  region: string | undefined
  tickmillCompany: number | undefined
}

const teamTypeOptions: Array<{ id: Option; name: Option }> = [
  { id: 'Custom', name: 'Custom' },
  { id: 'Region', name: 'Region' },
  { id: 'Country', name: 'Country' },
]

const AddEditTeamFormUI: React.FC<FormikProps<AddEditTeamFormValues> & OuterProps> = (props) => {
  const {
    handleSubmit,
    setSubmitting,
    isSubmitting,
    errors,
    regions,
    tickmillCompanies,
    countries,
    values,
    setFieldValue,
    onClose,
  } = props

  const { type } = values

  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  const handleTypeChange = (value: string | number) => {
    const nextType = String(value) as Option
    setFieldValue('type', nextType)

    if (nextType === 'Custom') {
      setFieldValue('country', undefined)
      setFieldValue('region', undefined)
      setFieldValue('tickmillCompany', undefined)
    }

    if (nextType === 'Region') {
      setFieldValue('country', undefined)
    }

    if (nextType === 'Country') {
      setFieldValue('region', undefined)
    }
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{values.id ? 'Edit Team' : 'Add Team'}</DialogTitle>
        </DialogHeader>
        <Form>
          <div className='flex flex-col gap-4'>
            <FormSelectField
              name='type'
              label='Type'
              value={values.type}
              options={teamTypeOptions.map((option) => ({
                ...option,
                disabled: !!values.id && option.id !== values.type,
              }))}
              onChange={handleTypeChange}
            />

            {type === 'Custom' && (
              <FormField name='name' label='Name' dataTest='team-name-field' error={errors.name} />
            )}

            {type === 'Region' && (
              <>
                <FormSelectField
                  name='region'
                  label='Region'
                  value={values.region}
                  options={regions.map((option) => ({
                    id: option.id,
                    name: option.name,
                  }))}
                  allowClear
                  showSearch
                  error={errors.region}
                  onChange={(value) => setFieldValue('region', String(value))}
                />
                <FormSelectField
                  name='tickmillCompany'
                  label='Company'
                  value={values.tickmillCompany}
                  options={tickmillCompanies.map((option) => ({
                    id: option.id,
                    name: option.name,
                  }))}
                  allowClear
                  showSearch
                  error={errors.tickmillCompany}
                  onChange={(value) =>
                    setFieldValue('tickmillCompany', value == null ? undefined : Number(value))
                  }
                />
              </>
            )}

            {type === 'Country' && (
              <>
                <FormSelectField
                  name='country'
                  label='Country'
                  value={values.country}
                  options={countries.map((option) => ({
                    id: option.id,
                    name: option.name,
                  }))}
                  allowClear
                  showSearch
                  error={errors.country}
                  onChange={(value) => setFieldValue('country', String(value))}
                />
                <FormSelectField
                  name='tickmillCompany'
                  label='Company'
                  value={values.tickmillCompany}
                  options={tickmillCompanies.map((option) => ({
                    id: option.id,
                    name: option.name,
                  }))}
                  allowClear
                  showSearch
                  error={errors.tickmillCompany}
                  onChange={(value) =>
                    setFieldValue('tickmillCompany', value == null ? undefined : Number(value))
                  }
                />
              </>
            )}
          </div>
        </Form>
        <DialogFooter>
          <Button disabled={isSubmitting} onClick={onClose} type='button' variant='outline'>
            Cancel
          </Button>
          <Button disabled={isSubmitting} onClick={handleSubmitForm} type='button'>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function dataToSubmit(values: AddEditTeamFormValues): FormSubmitValues<AddEditTeam> {
  return {
    id: values.id,
    tickmillCompany: values.tickmillCompany || null,
    region: values.region || null,
    country: values.country || null,
    name: values.name,
  }
}

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  team?: TeamDto
  regions: RegionDto[]
  tickmillCompanies: TickmillCompanyDto[]
  countries: CountryDto[]
  onSubmit(values: FormSubmitValues<AddEditTeam>): Promise<void>
}

const getTeamType = (team?: TeamDto): Option => {
  if (team?.country?.id) return 'Country'
  if (team?.region?.id) return 'Region'
  return 'Custom'
}

export const AddEditTeamForm = withFormik<OuterProps, AddEditTeamFormValues>({
  mapPropsToValues: ({ team }) => {
    const currentTeam: Partial<TeamDto> = team || {}
    return {
      type: getTeamType(team),
      id: currentTeam.id,
      name: currentTeam.name || '',
      country: currentTeam.country?.id || undefined,
      region: currentTeam.region?.id || undefined,
      tickmillCompany: currentTeam.tickmillCompany?.id || undefined,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddEditTeamFormValues> = {}
    if (values.type === 'Custom' && !values.name) {
      errors.name = 'required'
    }

    if (values.type === 'Country' && !values.country) {
      errors.country = 'required'
    }
    if (values.type === 'Region' && !values.region) {
      errors.region = 'required'
    }
    if (values.type !== 'Custom' && !values.tickmillCompany) {
      errors.tickmillCompany = 'required'
    }
    return errors
  },
  enableReinitialize: true,
})(AddEditTeamFormUI)
